'use client'

/**
 * SignupForm — refactor fast-food-loud paridad con LoginForm.
 *
 * Patrones DS:
 * - Inputs rounded-full + focus ring amarillo + border rojo
 * - Botón submit rojo brutalist con shadow [3px_3px_0_#1a1a1a]
 * - Show password toggle (👁/🙈) idéntico a LoginForm
 * - Loading spinner SVG inline
 * - Error rediseñado con icon ⚠ + animate-[slideInDown]
 * - Password strength meter (4 barras) + live checkmarks educativos
 * - Términos en color DS rojo (no azul)
 * - Login link como pill outline (espejo del CTA signup en /login)
 */
import { enviarSinVaciar } from '@/shared/lib/enviarSinVaciar'
import { useRef, useState, useTransition } from 'react'
import Link from 'next/link'
import { signupAction } from '@/features/auth/services/authActions'
import { trackEvent } from '@/shared/lib/plausible'

type StrengthScore = 0 | 1 | 2 | 3 | 4

const STRENGTH_LABELS: Record<StrengthScore, string> = {
  0: '',
  1: 'DÉBIL',
  2: 'MEDIA',
  3: 'FUERTE',
  4: 'ÉPICA',
}

// Colores fast-food-loud para las barras (visual fuerte)
const STRENGTH_BAR_COLORS: Record<StrengthScore, string> = {
  0: 'transparent',
  1: '#da291c',
  2: '#f97316',
  3: '#ffc72c',
  4: '#16a34a',
}

// Colores oscuros para el label (WCAG AA sobre cream #fff8e7)
// El amarillo #ffc72c sobre cream falla AA — usamos yellow-800 dark equivalent
const STRENGTH_LABEL_COLORS: Record<StrengthScore, string> = {
  0: '#1a1a1a',
  1: '#b91c1c', // red-700
  2: '#c2410c', // orange-700
  3: '#854d0e', // yellow-800 (legible sobre cream)
  4: '#15803d', // green-700
}

function getPasswordScore(password: string): StrengthScore {
  if (!password) return 0
  let score = 0
  if (password.length >= 8) score++
  if (/[A-Z]/.test(password)) score++
  if (/[0-9]/.test(password)) score++
  if (/[^A-Za-z0-9]/.test(password)) score++
  return score as StrengthScore
}

export function SignupForm({
  /** PRP-017 F4: plan que el prospecto tocó en `/pricing`, ya validado
   *  contra BD por la page. Solo se arrastra — no cambia el alta. */
  selectedPlanId = null,
}: {
  selectedPlanId?: string | null
} = {}) {
  const [error, setError] = useState<string | null>(null)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isPending, startTransition] = useTransition()
  const [showPassword, setShowPassword] = useState(false)
  const [password, setPassword] = useState('')
  const errorRef = useRef<HTMLParagraphElement>(null)

  // PRP-017 F7 — `signup_start`. Se dispara con la PRIMERA tecla real, no al
  // montar: montar es lo mismo que la visita a /signup, que Plausible ya cuenta
  // sola. Lo que no se puede deducir de la visita es cuanta gente llega al
  // formulario y ni siquiera empieza a llenarlo, que es donde se cae el embudo.
  const inicioEnviado = useRef(false)
  function marcarInicio() {
    if (inicioEnviado.current) return
    inicioEnviado.current = true
    trackEvent('signup_start', selectedPlanId ? { plan: selectedPlanId } : undefined)
  }

  const score = getPasswordScore(password)
  const checks = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    digit: /[0-9]/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  }

  function onSubmit(formData: FormData) {
    setError(null)
    const passwordValue = String(formData.get('password') ?? '')
    if (passwordValue.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.')
      requestAnimationFrame(() => {
        errorRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      })
      return
    }
    if (!termsAccepted) {
      setError('Debes aceptar los Términos y el Aviso de Privacidad para continuar.')
      requestAnimationFrame(() => {
        errorRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      })
      return
    }
    startTransition(async () => {
      const result = await signupAction({
        email: String(formData.get('email') ?? ''),
        password: passwordValue,
        fullName: String(formData.get('fullName') ?? ''),
        restaurantName: String(formData.get('restaurantName') ?? ''),
        ownerPhone: String(formData.get('ownerPhone') ?? '').trim() || undefined,
        selectedPlanId,
        termsAccepted,
      })
      if (!result.ok) {
        setError(result.error)
        requestAnimationFrame(() => {
          errorRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        })
      }
    })
  }

  return (
    // `onInput` en el form aprovecha el burbujeo: una sola linea cubre los 5
    // campos, sin tocar ni un input. `capture` no hace falta.
    <form onSubmit={(e) => enviarSinVaciar(e, onSubmit)} onInput={marcarInicio} className="space-y-4">
      {/* Nombre del negocio */}
      <div className="space-y-1.5">
        <label
          htmlFor="restaurantName"
          className="text-xs font-black uppercase tracking-widest text-[#da291c] block"
        >
          Nombre del negocio
        </label>
        <input
          id="restaurantName"
          name="restaurantName"
          type="text"
          autoCapitalize="words"
          autoComplete="organization"
          required
          placeholder="Ej: Tacos La Esquina"
          className="w-full rounded-full bg-white border-2 border-[#1a1a1a]/15 px-4 py-2.5 min-h-[44px] text-base sm:text-sm font-bold tracking-tight placeholder:text-[#1a1a1a]/30 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 focus-visible:border-[#da291c] hover:border-[#1a1a1a] transition-[border-color,box-shadow] duration-200 [touch-action:manipulation]"
        />
      </div>

      {/* Tu nombre */}
      <div className="space-y-1.5">
        <label
          htmlFor="fullName"
          className="text-xs font-black uppercase tracking-widest text-[#da291c] block"
        >
          Cómo te llamas
        </label>
        <input
          id="fullName"
          name="fullName"
          type="text"
          autoCapitalize="words"
          autoComplete="name"
          required
          placeholder="Ej: Carlos Méndez"
          className="w-full rounded-full bg-white border-2 border-[#1a1a1a]/15 px-4 py-2.5 min-h-[44px] text-base sm:text-sm font-bold tracking-tight placeholder:text-[#1a1a1a]/30 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 focus-visible:border-[#da291c] hover:border-[#1a1a1a] transition-[border-color,box-shadow] duration-200 [touch-action:manipulation]"
        />
      </div>

      {/* PRP-088 V3 F19: tu WhatsApp para alertas operativas */}
      <div className="space-y-1.5">
        <label
          htmlFor="ownerPhone"
          className="text-xs font-black uppercase tracking-widest text-[#da291c] block"
        >
          Tu WhatsApp (opcional)
        </label>
        <input
          id="ownerPhone"
          name="ownerPhone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+52 962 123 4567"
          className="w-full rounded-full bg-white border-2 border-[#1a1a1a]/15 px-4 py-2.5 min-h-[44px] text-base sm:text-sm font-bold tracking-tight placeholder:text-[#1a1a1a]/30 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 focus-visible:border-[#da291c] hover:border-[#1a1a1a] transition-[border-color,box-shadow] duration-200 [touch-action:manipulation]"
        />
        <p className="text-[10px] text-[#1a1a1a]/50">
          Recibirás alertas operativas (cuota de WhatsApp/Bot al 80%/100%) por
          aquí. Si lo dejas vacío, te llegarán por email.
        </p>
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label
          htmlFor="email"
          className="text-xs font-black uppercase tracking-widest text-[#da291c] block"
        >
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          placeholder="tu@restaurante.com"
          className="w-full rounded-full bg-white border-2 border-[#1a1a1a]/15 px-4 py-2.5 min-h-[44px] text-base sm:text-sm font-bold tracking-tight placeholder:text-[#1a1a1a]/30 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 focus-visible:border-[#da291c] hover:border-[#1a1a1a] transition-[border-color,box-shadow] duration-200 [touch-action:manipulation]"
        />
      </div>

      {/* Password con toggle + strength meter + checkmarks */}
      <div className="space-y-1.5">
        <label
          htmlFor="password"
          className="text-xs font-black uppercase tracking-widest text-[#da291c] block"
        >
          Contraseña
        </label>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            className="w-full rounded-full bg-white border-2 border-[#1a1a1a]/15 px-4 py-2.5 pr-14 min-h-[44px] text-base sm:text-sm font-bold tracking-tight placeholder:text-[#1a1a1a]/30 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 focus-visible:border-[#da291c] hover:border-[#1a1a1a] transition-[border-color,box-shadow] duration-200 [touch-action:manipulation]"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-11 h-11 sm:w-9 sm:h-9 rounded-full text-[#1a1a1a]/40 hover:text-[#da291c] hover:bg-[#fff8e7] active:scale-90 transition-[transform,background-color,color] duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 [touch-action:manipulation]"
          >
            {showPassword ? (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 00-1.06 1.06l14.5 14.5a.75.75 0 101.06-1.06l-1.745-1.745a10.029 10.029 0 003.3-4.38 1.651 1.651 0 000-1.185A10.004 10.004 0 009.999 3a9.956 9.956 0 00-4.744 1.194L3.28 2.22zM7.752 6.69l1.092 1.092a2.5 2.5 0 013.374 3.373l1.091 1.092a4 4 0 00-5.557-5.557z" clipRule="evenodd" />
                <path d="M10.748 13.93l2.523 2.523a9.987 9.987 0 01-3.27.547c-4.258 0-7.894-2.66-9.337-6.41a1.651 1.651 0 010-1.186A10.007 10.007 0 012.839 6.02L6.07 9.252a4 4 0 004.678 4.678z" />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
                <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>

        {/* Strength meter — 4 barras + label dinámica */}
        {password.length > 0 && (
          <div className="pt-1.5 space-y-1.5" aria-live="polite">
            <div className="flex gap-1">
              {[1, 2, 3, 4].map((segment) => (
                <div
                  key={segment}
                  className="h-2 sm:h-1.5 flex-1 rounded-full overflow-hidden border border-[#1a1a1a]/10 bg-[#1a1a1a]/[0.04]"
                >
                  <div
                    className="h-full origin-left transition-transform duration-300 ease-out"
                    style={{
                      backgroundColor: STRENGTH_BAR_COLORS[score],
                      transform: segment <= score ? 'scaleX(1)' : 'scaleX(0)',
                      transitionDelay: segment <= score ? `${(segment - 1) * 60}ms` : '0ms',
                    }}
                  />
                </div>
              ))}
            </div>
            <p
              className="text-[11px] font-black uppercase tracking-widest transition-colors duration-200"
              style={{ color: STRENGTH_LABEL_COLORS[score] }}
            >
              {score === 0 ? ' ' : `Fuerza: ${STRENGTH_LABELS[score]}`}
            </p>
          </div>
        )}

        {/* Live checkmarks educativos */}
        <ul className="grid grid-cols-2 gap-1.5 pt-1" aria-label="Requisitos de contraseña">
          <Check label="8+ caracteres" met={checks.length} />
          <Check label="Una mayúscula" met={checks.upper} />
          <Check label="Un número" met={checks.digit} />
          <Check label="Un símbolo" met={checks.symbol} />
        </ul>
      </div>

      {/* Términos checkbox + links DS */}
      <label className="flex items-start gap-2 cursor-pointer text-xs font-bold text-[#1a1a1a]/80 select-none min-h-[44px] sm:min-h-0 py-2 px-2 -mx-2 sm:py-0 sm:px-0 sm:mx-0 rounded-lg active:bg-[#da291c]/5 sm:active:bg-transparent transition-colors duration-150 [touch-action:manipulation]">
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="mt-[3px] sm:mt-0.5 w-5 h-5 sm:w-4 sm:h-4 accent-[#da291c] cursor-pointer flex-shrink-0"
        />
        <span className="leading-relaxed sm:leading-snug">
          Al crear cuenta acepto los{' '}
          <Link
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="font-black uppercase tracking-wider text-[#da291c] hover:text-[#1a1a1a] active:scale-95 transition-[color,transform] duration-150 [touch-action:manipulation]"
          >
            Términos
          </Link>
          {' '}y{' '}
          <Link
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="font-black uppercase tracking-wider text-[#da291c] hover:text-[#1a1a1a] active:scale-95 transition-[color,transform] duration-150 [touch-action:manipulation]"
          >
            Aviso de Privacidad
          </Link>
          . Protegemos tus datos y los de tus clientes.
        </span>
      </label>

      {/* Error message brutalist */}
      {error && (
        <p
          ref={errorRef}
          role="alert"
          aria-live="polite"
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[#da291c] bg-[#da291c]/10 border-2 border-[#da291c]/30 rounded-[1rem] px-3 py-2 w-full animate-[slideInDown_200ms_ease-out]"
        >
          <span aria-hidden="true">⚠</span>
          {error}
        </p>
      )}

      {/* Submit brutalist + spinner */}
      <button
        type="submit"
        disabled={isPending || !termsAccepted}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#da291c] text-white border-2 border-[#1a1a1a] px-4 py-3 min-h-[48px] text-sm font-black uppercase tracking-widest active:scale-95 hover:bg-[#1a1a1a] transition-[transform,background-color,box-shadow,border-color,color] duration-200 hover:shadow-[0_0_24px_-6px_#da291c] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 [touch-action:manipulation] shadow-[3px_3px_0_#1a1a1a] disabled:bg-transparent disabled:text-[#1a1a1a]/70 disabled:border-[#ffc72c] disabled:shadow-[3px_3px_0_#ffc72c] disabled:cursor-not-allowed"
      >
        {isPending ? (
          <>
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            Creando cuenta...
          </>
        ) : !termsAccepted ? (
          <>👆 Acepta términos primero</>
        ) : (
          <>🎉 Empezar 1 mes gratis →</>
        )}
      </button>

      {/* Divider + login link como pill outline (espejo del signup en /login) */}
      <div className="pt-4 mt-3 sm:pt-2 sm:mt-2 border-t-2 border-dashed border-[#1a1a1a]/10 text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-[#1a1a1a]/50">
          ¿Ya tienes cuenta?
        </p>
        <Link
          href="/login"
          className="inline-flex items-center justify-center gap-1.5 mt-2 rounded-full bg-white text-[#da291c] border-2 border-[#da291c] px-4 py-2 min-h-[44px] sm:min-h-[40px] text-xs font-black uppercase tracking-widest hover:bg-[#da291c] hover:text-white active:scale-95 transition-[transform,background-color,color] duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#ffc72c]/50 [touch-action:manipulation]"
        >
          Entrar a mi negocio
        </Link>
      </div>
    </form>
  )
}

function Check({ label, met }: { label: string; met: boolean }) {
  return (
    <li
      className={`inline-flex items-center gap-1.5 text-xs sm:text-[11px] font-black uppercase tracking-wider transition-colors duration-200 ${
        met ? 'text-[#16a34a]' : 'text-[#1a1a1a]/40'
      }`}
    >
      <span
        // key forza re-mount al cambiar met → re-dispara la animación pop
        key={met ? 'met' : 'unmet'}
        aria-hidden="true"
        className={`inline-flex items-center justify-center w-4 h-4 sm:w-3.5 sm:h-3.5 rounded-full border-2 flex-shrink-0 transition-colors duration-200 ${
          met
            ? 'bg-[#16a34a] border-[#16a34a] text-white animate-[pop_240ms_ease-out]'
            : 'bg-transparent border-[#1a1a1a]/20'
        }`}
      >
        {met && (
          <svg viewBox="0 0 12 12" fill="none" className="w-2.5 h-2.5 sm:w-2 sm:h-2">
            <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {label}
    </li>
  )
}
